import type { RetailerDto, SubBrandDto } from "../../../../contracts";
import type { CommercialSimulatorSubBrandToolRequest } from "../../../../models/toolRequestModels";
import type { CommercialSimulatorSubBrandToolResponse } from "../../../../models/toolResponseModels";
import type { SubBrandSetupSelection } from "../hooks/useSubBrandSetupData";

type ResolveInitialSubBrandSelectionOptions = {
    persistedSelection: CommercialSimulatorSubBrandToolResponse["tool_call_user_selection"] | null | undefined;
    input: CommercialSimulatorSubBrandToolRequest["input"];
    retailers: RetailerDto[];
    subBrands: SubBrandDto[];
};

const normalizeSelectionId = (
    value: number | string | null | undefined
): number | null => {
    if (value == null || String(value).trim() === "") {
        return null;
    }

    const id: number = Number(value);

    return Number.isNaN(id) ? null : id;
};

const findSelectionMatch = <T extends { id: number; name: string }>(
    items: readonly T[],
    id: number | string | null | undefined,
    name: string | null | undefined
): T | null => {
    const normalizedId = normalizeSelectionId(id);
    const normalizedName = name?.trim().toLowerCase();

    return items.find((item: T): boolean => item.id === normalizedId)
        ?? (
            normalizedName
                ? items.find(
                    (item: T): boolean =>
                        item.name.trim().toLowerCase() === normalizedName
                )
                : null
        )
        ?? null;
};

export const resolveInitialSubBrandSelection = ({
    persistedSelection,
    input,
    retailers,
    subBrands
}: ResolveInitialSubBrandSelectionOptions): SubBrandSetupSelection => {
    const requestedRetailer = input?.retailers?.[0];

    const retailer = findSelectionMatch(
        retailers,
        persistedSelection?.retailer_id,
        persistedSelection?.retailer_name
    ) ?? findSelectionMatch(
        retailers,
        requestedRetailer?.retailer_id,
        requestedRetailer?.retailer_name
    );

    const subBrand = findSelectionMatch(
        subBrands,
        persistedSelection?.sub_brand_id,
        persistedSelection?.sub_brand_name
    ) ?? findSelectionMatch(
        subBrands,
        input?.sub_brand_id,
        input?.product_name?.base_name
    );

    return {
        brandId: subBrand?.brand_id ?? null,
        subBrandId: subBrand?.id ?? null,
        retailerId: retailer?.id ?? null,
        packSizeKey: persistedSelection?.pack_size_key
            ?? input?.pack_size_key
            ?? null
    };
};